import { useCallback, useEffect, useRef, useState } from 'react';
import { getCandidatePosition, type ICandidatePosition } from '~/components/ConfirmCandidate/utils';
import { IMultipleCounters } from '~/models';
import {
  fireConfirmCandidateCandidateViewed,
  fireConfirmCandidateLeaveClicked,
} from '~/posthogs/events';

/** The position reported by the last impression, kept so outcomes can join to it. */
interface IViewedPosition {
  candidateId: string;
  position: ICandidatePosition;
}

interface IUseCandidateImpressionArgs {
  shiftId?: string;
  /** Every counter offer on the shift, in the order the office sees them. */
  counters: IMultipleCounters[];
  /** The counter whose page is open, or `null` while the list is showing. */
  activeCounter: IMultipleCounters | null;
}

/**
 * The `confirm_candidate` impression, and the position every later outcome
 * reports.
 *
 * `candidate_viewed` fires once per distinct candidate opened — including the
 * sole applicant on arrival, which is the legacy screen's "default candidate on
 * open". Opening a candidate is what viewing one means here: on the list the
 * office has seen a row, not a professional.
 *
 * The position is frozen at that moment rather than recomputed at click time.
 * Declining a candidate invalidates the counter list, so by the time the office
 * confirms the survivor the list has shrunk — recomputing then reported her as
 * "1 of 1" when her own impression, moments earlier, said "2 of 2". That breaks
 * the impression↔outcome join and inverts the question the data exists to answer
 * (GOTU-4816).
 *
 * Both events require a shift id and an offer id, so both call sites narrow to
 * them rather than asserting: the impression genuinely cannot be reported for a
 * candidate the page has not resolved yet.
 */
export const useCandidateImpression = ({
  shiftId,
  counters,
  activeCounter,
}: IUseCandidateImpressionArgs) => {
  const [viewedPosition, setViewedPosition] = useState<IViewedPosition | null>(null);
  const lastViewedCandidateIdRef = useRef<string | null>(null);

  const candidateId = activeCounter?.candidate?.id;
  const offerId = activeCounter?.id;

  useEffect(() => {
    if (!shiftId || !candidateId || !offerId || counters.length === 0) return;
    if (lastViewedCandidateIdRef.current === candidateId) return;

    lastViewedCandidateIdRef.current = candidateId;

    const position = getCandidatePosition(counters, candidateId);
    setViewedPosition({ candidateId, position });

    fireConfirmCandidateCandidateViewed({
      shiftId,
      offerId,
      professionalId: candidateId,
      ...position,
      // `getCandidatePosition` leaves the count optional for a candidate it
      // cannot locate; the impression's contract requires it, and a candidate
      // being viewed is by definition in the list it was opened from.
      candidateCount: position.candidateCount,
    });
  }, [candidateId, counters, offerId, shiftId]);

  /**
   * Fires on "Leave Anyway" from the exit popup (GOTU-5248), before the deferred
   * navigation runs. Falls back to a live derivation when no impression has been
   * recorded for this candidate — there is nothing to join to in that case, so a
   * fresh position is the best available.
   */
  const reportLeaveClicked = useCallback(() => {
    if (!shiftId) return;

    const positionAtImpression =
      viewedPosition !== null && viewedPosition.candidateId === candidateId
        ? viewedPosition.position
        : getCandidatePosition(counters, candidateId);

    fireConfirmCandidateLeaveClicked({
      shiftId,
      professionalId: candidateId,
      offerId,
      ...positionAtImpression,
    });
  }, [candidateId, counters, offerId, shiftId, viewedPosition]);

  return { viewedPosition, reportLeaveClicked };
};
